import * as React from 'react';

import Retracked from 'js/app/retracked';

import SettingsMenuButton from 'bundles/general-video-player/components/control-bar/settingsMenu/SettingsMenuButton';
import SettingsMenuPopup from 'bundles/general-video-player/components/control-bar/settingsMenu/SettingsMenuPopup';
import type { VideoPlayer } from 'bundles/item-lecture';
import constants from 'bundles/video-player/constants';
import userPreferences from 'bundles/video-player/utils/userPreferences';
import { getDefaultVideoResolution } from 'bundles/video-player/utils/videoUtils';

import 'css!./__styles__/VideoSettingsMenu';

type Props = {
  onSettingsMenuClick: () => void;
  assignRef: (node: HTMLElement | null) => void;
  menuPopupVisible: boolean;
  player: VideoPlayer;
  hideAutoPlayToggle?: boolean;
  isAutoPlayEnabled: boolean;
  onAutoPlayChange?: (isAutoPlayEnabled: boolean) => void;
};

export type ResolutionOption = '240p' | '360p' | '540p' | '720p';
type State = {
  currentPlaybackRate: 0.75 | 1 | 1.25 | 1.5 | 1.75 | 2 | null;
  currentResolution: ResolutionOption | null;
  supportedResolutions: ResolutionOption[] | null;
};

const EVENTING_CONTEXT_DATA = {
  namespace: {
    app: 'open_course',
    page: 'video',
  },
};

class VideoSettingsMenu extends React.Component<Props, State> {
  declare videoSettingsRef: HTMLElement | null | undefined;

  state: State = {
    currentPlaybackRate: null,
    currentResolution: null,
    supportedResolutions: null,
  };

  componentDidMount() {
    const { player } = this.props;

    this.setState({
      currentPlaybackRate: player.playbackRate(),
      currentResolution: player.resolution(),
      supportedResolutions: player.getSupportedResolutions(),
    });

    player.on('resolutionchange', this.onResolutionChange);
    player.on('ratechange', this.onPlaybackRateChange);
  }

  componentWillUnmount() {
    const { player } = this.props;

    player.off('resolutionchange', this.onResolutionChange);
    player.off('ratechange', this.onPlaybackRateChange);
  }

  onPlaybackRateChange = () => {
    const { player } = this.props;
    const currentPlaybackRate = player ? player.playbackRate() : 1;

    this.setState({ currentPlaybackRate });
    userPreferences.set('playbackRate', currentPlaybackRate);
  };

  onResolutionChange = () => {
    const { player } = this.props;
    const currentResolution = player ? player.resolution() : getDefaultVideoResolution();

    this.setState({ currentResolution });
    userPreferences.set('resolution', currentResolution);
  };

  onIncreaseResolutionClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    const { player } = this.props;
    player.increaseResolution();
  };

  onDecreaseResolutionClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    const { player } = this.props;
    player.decreaseResolution();
  };

  onIncreasePlaybackRateClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    const { player } = this.props;
    const { currentPlaybackRate } = this.state;
    const { playbackRates } = constants;

    const currentPlaybackRateIndex = playbackRates.findIndex((rate) => rate === currentPlaybackRate);
    const newPlaybackRateIndex = Math.min(playbackRates.length - 1, currentPlaybackRateIndex + 1);
    const newPlaybackRate = playbackRates[newPlaybackRateIndex];

    player.playbackRate(newPlaybackRate);
  };

  onDecreasePlaybackRateClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    const { player } = this.props;
    const { currentPlaybackRate } = this.state;
    const { playbackRates } = constants;

    const currentPlaybackRateIndex = playbackRates.findIndex((rate) => rate === currentPlaybackRate);
    const newPlaybackRateIndex = Math.max(0, currentPlaybackRateIndex - 1);
    const newPlaybackRate = playbackRates[newPlaybackRateIndex];

    player.playbackRate(newPlaybackRate);
  };

  onEnableAutoplayClick = () => {
    const { player } = this.props;
    userPreferences.set('autoplay', true);
    player.trigger('autoplaychange');
    this.logAutoplayChangeEvent(true);
  };

  onDisableAutoplayClick = () => {
    const { player } = this.props;
    userPreferences.set('autoplay', false);
    player.trigger('autoplaychange');
    this.logAutoplayChangeEvent(false);
  };

  // While we do get an event from player.trigger('autoplaychange'), the event data doesn't include the autoplay state.
  // Logging an additional event as the quickest way to get the autoplay value into the events table, to support an experiment with autoplay.
  logAutoplayChangeEvent = (newValue: boolean) => {
    const trackingData = { autoplay: newValue };
    Retracked.trackComponent(EVENTING_CONTEXT_DATA, trackingData, 'autoplay_toggle_in_settings_menu', 'click');
  };

  render() {
    const {
      onSettingsMenuClick,
      assignRef,
      player,
      menuPopupVisible,
      hideAutoPlayToggle,
      isAutoPlayEnabled,
      onAutoPlayChange,
    } = this.props;
    const { currentPlaybackRate, currentResolution, supportedResolutions } = this.state;

    // don't render if state hasn't been initialized from player
    if (currentPlaybackRate === null || currentResolution === null || supportedResolutions === null) {
      return null;
    }

    return (
      <div ref={assignRef} className="rc-VideoSettingsMenu">
        <SettingsMenuButton player={player} menuPopupVisible={menuPopupVisible} onClick={onSettingsMenuClick} />

        <SettingsMenuPopup
          visible={menuPopupVisible}
          onIncreasePlaybackRateClick={this.onIncreasePlaybackRateClick}
          onDecreasePlaybackRateClick={this.onDecreasePlaybackRateClick}
          playbackRate={currentPlaybackRate}
          onIncreaseResolutionClick={this.onIncreaseResolutionClick}
          onDecreaseResolutionClick={this.onDecreaseResolutionClick}
          currentResolution={currentResolution}
          supportedResolutions={supportedResolutions}
          isAutoPlayEnabled={isAutoPlayEnabled}
          onAutoPlayChange={onAutoPlayChange}
          hideAutoPlayToggle={hideAutoPlayToggle}
        />
      </div>
    );
  }
}

export default VideoSettingsMenu;
